import { type FormEvent, useState } from 'react';
import { useTracking } from 'react-tracking';

import { Button, TextField } from '@simplywallst/ui-core/next';

import { useSnackbar } from '@/features/snackbar';

import useUser from '@/hooks/useUser';
import { usePromoCode } from './hooks/usePromoCode';
import { useSubmitPromoCode } from './hooks/useSubmitPromoCode';

const formatPlan = (plan: string): string =>
  plan.charAt(0).toUpperCase() + plan.slice(1);

export const RedeemForm = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const promoCodeParam = (
    searchParams.get('coupon') ?? searchParams.get('code')
  )?.trim();

  const [promoCode, setPromoCode] = useState(promoCodeParam ?? '');
  const { addSnackbar } = useSnackbar();
  const { trackEvent } = useTracking();

  const { mutateAsync: getPromoCode, isPending: isGetPromoCodePending } =
    usePromoCode();
  const {
    mutateAsync,
    isSuccess,
    isPending: isSubmitPromoCodePending,
  } = useSubmitPromoCode();
  const { refetch: refetchUser } = useUser();

  const isPending = isGetPromoCodePending || isSubmitPromoCodePending;

  const handleRedeem = async (e: FormEvent<HTMLFormElement> | undefined) => {
    e?.preventDefault();
    try {
      trackEvent({ modifier: 'redeem', action: 'click' });
      const trimmedPromoCode = promoCode.trim();
      const promoCodeData = await getPromoCode(trimmedPromoCode);
      await mutateAsync({ promoCode: trimmedPromoCode });
      refetchUser();
      addSnackbar({
        type: 'positive',
        message: `Gift card redeemed successfully. You are now subscribed to ${formatPlan(
          promoCodeData.plan_type
        )} Annual plan`,
        lifeTime: 5000,
      });
    } catch (_) {
      addSnackbar({
        type: 'negative',
        message: 'Failed to redeem the gift card',
        lifeTime: 5000,
      });
    }
  };

  return (
    <form className="grid w-72 gap-4" onSubmit={handleRedeem}>
      <TextField
        aria-label="Gift card code"
        placeholder="Enter your gift card code"
        value={promoCode}
        onChange={(value) => setPromoCode(value ?? '')}
      />
      <Button
        isDisabled={isPending || isSuccess}
        isLoading={isPending}
        variant="secondary"
        type="submit"
      >
        Redeem
      </Button>
    </form>
  );
};
